import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popover2 } from '@evertrue/et-components';

const NUMERIC_OPERATORS = [
  { value: 'all', label: 'All' },
  { value: 'eq', label: 'Is equal to' },
  { value: 'neq', label: 'Is not equal to' },
  { value: 'gt', label: 'Is greater than' },
  { value: 'lt', label: 'Is less than' },
  { value: 'between', label: 'Is between' },
  { value: 'not_between', label: 'Is not between' },
];

const OPERATORS_WITH_SINGLE_VALUE = ['eq', 'neq', 'gt', 'lt'];
const OPERATORS_WITH_RANGE = ['between', 'not_between'];

/**
 * Numeric filter popover with operator selection and value input(s).
 * Emits a filter config { operator, value1, value2 } that the parent uses for filtering.
 */
const NumericFilterPopover = ({ value, onChange, label = 'Count' }) => {
  const [isOpen, setIsOpen] = useState(false);

  const displayLabel = () => {
    const op = NUMERIC_OPERATORS.find((o) => o.value === value.operator);
    if (!op || value.operator === 'all') return label;
    if (OPERATORS_WITH_SINGLE_VALUE.includes(value.operator) && value.value1 != null && value.value1 !== '') {
      return `${label} | ${op.label} ${value.value1}`;
    }
    if (OPERATORS_WITH_RANGE.includes(value.operator) && value.value1 != null && value.value2 != null) {
      return `${label} | ${op.label} ${value.value1} – ${value.value2}`;
    }
    return `${label} | ${op.label}`;
  };

  const handleOperatorChange = (operator) => {
    if (operator === 'all') {
      onChange({ operator: 'all', value1: null, value2: null });
      setIsOpen(false);
    } else {
      onChange({ ...value, operator });
    }
  };

  const handleApply = () => {
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange({ operator: 'all', value1: null, value2: null });
    setIsOpen(false);
  };

  const needsInput =
    OPERATORS_WITH_SINGLE_VALUE.includes(value.operator) || OPERATORS_WITH_RANGE.includes(value.operator);
  const needsSecondValue = OPERATORS_WITH_RANGE.includes(value.operator);
  const isActive = value.operator !== 'all';

  return (
    <Popover2
      trigger={
        <Button type="simple" className={`cadences-v2-filters--btn ${isActive ? 'is-active' : ''}`}>
          <span>{displayLabel()}</span>
          <Icon icon="chevron-down" size={1} />
        </Button>
      }
      contentClassName="cadences-v2-numeric-filter"
      isOpen={isOpen}
      onToggle={setIsOpen}
    >
      {() => (
        <div className="cadences-v2-numeric-filter--menu">
          <div className="cadences-v2-numeric-filter--header">Filter by Number</div>
          <div className="cadences-v2-numeric-filter--operators">
            {NUMERIC_OPERATORS.map((op) => (
              <button
                key={op.value}
                type="button"
                className={`cadences-v2-numeric-filter--op ${value.operator === op.value ? 'is-selected' : ''}`}
                onClick={() => handleOperatorChange(op.value)}
              >
                {op.label}
              </button>
            ))}
          </div>
          {needsInput && (
            <div
              className="cadences-v2-numeric-filter--inputs"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApply();
              }}
            >
              <input
                type="number"
                className="cadences-v2-numeric-filter--number-input"
                value={value.value1 ?? ''}
                placeholder="Value"
                onChange={(e) => onChange({ ...value, value1: e.target.value === '' ? null : Number(e.target.value) })}
              />
              {needsSecondValue && (
                <>
                  <span className="cadences-v2-numeric-filter--separator">to</span>
                  <input
                    type="number"
                    className="cadences-v2-numeric-filter--number-input"
                    value={value.value2 ?? ''}
                    placeholder="Value"
                    onChange={(e) =>
                      onChange({ ...value, value2: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </>
              )}
              <Button type="action" size="small" onClick={handleApply}>
                Apply
              </Button>
            </div>
          )}
          {isActive && (
            <button type="button" className="cadences-v2-numeric-filter--clear" onClick={handleClear}>
              Clear Filter
            </button>
          )}
        </div>
      )}
    </Popover2>
  );
};

/**
 * Apply a numeric filter config to a number value.
 * @param {number|null} num - The number to test
 * @param {{ operator: string, value1: number|null, value2: number|null }} filter
 * @returns {boolean} true if the number passes the filter
 */
export const matchesNumericFilter = (num, filter) => {
  if (!filter || filter.operator === 'all') return true;
  if (num == null) return false;

  switch (filter.operator) {
    case 'eq':
      return filter.value1 != null ? num === filter.value1 : true;
    case 'neq':
      return filter.value1 != null ? num !== filter.value1 : true;
    case 'gt':
      return filter.value1 != null ? num > filter.value1 : true;
    case 'lt':
      return filter.value1 != null ? num < filter.value1 : true;
    case 'between':
      if (filter.value1 == null || filter.value2 == null) return true;
      return num >= filter.value1 && num <= filter.value2;
    case 'not_between':
      if (filter.value1 == null || filter.value2 == null) return true;
      return num < filter.value1 || num > filter.value2;
    default:
      return true;
  }
};

NumericFilterPopover.propTypes = {
  value: PropTypes.shape({
    operator: PropTypes.string.isRequired,
    value1: PropTypes.number,
    value2: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default NumericFilterPopover;
